'use client';

import React, { useEffect, useState, useRef } from 'react';
import Link from 'next/link';

export default function BridgeIRLHero() {
  const [loaded, setLoaded] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const handleMouseMove = (e: MouseEvent) => {
      if (cursorRef.current) {
        cursorRef.current.style.left = `${e.clientX}px`;
        cursorRef.current.style.top = `${e.clientY}px`;
      }
    };
    section.addEventListener('mousemove', handleMouseMove);
    return () => section.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section ref={sectionRef} className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden hero-gradient-bg grain-overlay pt-20">
      <div
        ref={cursorRef}
        style={{ position: 'fixed', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(139,105,20,0.1) 0%, transparent 65%)', pointerEvents: 'none', transform: 'translate(-50%, -50%)', zIndex: 2, willChange: 'transform' }}
      />
      <div className="pixel-border-gold-top" />
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(139,105,20,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(139,105,20,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px', zIndex: 0 }} />
      {/* Expanding circles diagram */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <svg viewBox="-300 -300 600 600" width="700" height="700" style={{ opacity: 0.25 }}>
          {[40, 80, 130, 190, 260]?.map((r, i) => (
            <circle key={r} cx="0" cy="0" r={r}
              fill="none" stroke="rgba(139,105,20,0.5)"
              strokeWidth={i === 0 ? '2' : '0.5'}
              opacity={1 - i * 0.12}
            />
          ))}
          {Array.from({ length: 12 }, (_, i) => {
            const angle = (i / 12) * 360;
            const rad = (angle * Math.PI) / 180;
            return (
              <line key={i}
                x1={40 * Math.cos(rad)} y1={40 * Math.sin(rad)}
                x2={260 * Math.cos(rad)} y2={260 * Math.sin(rad)}
                stroke="rgba(139,105,20,0.3)" strokeWidth="0.3" opacity="1"
              />
            );
          })}
          <circle cx="0" cy="0" r="35" fill="#1C2B25" stroke="rgba(179,138,42,0.5)" strokeWidth="1.5" />
          <text x="0" y="-5" textAnchor="middle" fill="#F5F0E8" fontSize="8" fontFamily="monospace" fontWeight="900">BRIDGE</text>
          <text x="0" y="8" textAnchor="middle" fill="rgba(179,138,42,0.7)" fontSize="6" fontFamily="monospace" opacity="1">02</text>
        </svg>
      </div>
      <div className={`relative z-10 max-w-[900px] mx-auto px-6 text-center transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: 'rgba(139,105,20,0.5)' }}>Bridge 02</p>
        <p className="font-mono text-xs tracking-widest uppercase mb-6" style={{ color: 'rgba(245,240,232,0.3)' }}>Ayat in Creation → IRL Application</p>
        <h1 className="font-mono text-5xl md:text-6xl font-black uppercase tracking-tight leading-none mb-6" style={{ color: '#F5F0E8' }}>
          FROM SEEING<br />
          <span className="text-shimmer">TO DOING</span>
        </h1>
        <p className="font-mono text-sm max-w-lg mx-auto leading-relaxed mb-12" style={{ color: 'rgba(245,240,232,0.5)' }}>
          The shift from recognition to utility. Perceiving signs translates into real-world action. The inner circle of awareness expands outward into the world of application.
        </p>
        <div className="flex items-center justify-center gap-6">
          <Link href="#irl-content" className="font-mono text-xs tracking-widest uppercase px-6 py-3 transition-colors"
            style={{ background: 'rgba(139,105,20,0.9)', color: '#F5F0E8', border: '1px solid rgba(179,138,42,0.4)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(179,138,42,0.95)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(139,105,20,0.9)'; }}
          >
            Cross the Bridge
          </Link>
          <Link href="/inner-reality-lens" className="font-mono text-xs tracking-widest uppercase transition-colors border-b"
            style={{ color: 'rgba(245,240,232,0.4)', borderColor: 'rgba(139,105,20,0.3)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(196,149,106,0.9)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.4)'; }}
          >
            Next: Inner Reality Lens →
          </Link>
        </div>
      </div>
    </section>
  );
}
